import { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Button from "components/Buttons/Button/Button";
import InputField from "components/InputField/InputField";
import Loading from "components/Loading/Loading";
import useSnackbar from "hooks/useSnackbar";
import OrderService from "services/OrderService";
import ButtonCloseModal from "components/Buttons/ButtonCloseModal/ButtonCloseModal";
import { Color } from "enums/Common";
import { IOrderDetails, IOrderVoidInfo } from "interfaces/Order";
import { formatDate } from "helpers/CommonHelper";
import { getVoidChargeLines } from "helpers/OrderHelper";

interface ReviewVoidRequestModalProps {
  order: IOrderDetails;
  onClose: () => void;
  onSuccess: () => void;
}

enum Decision {
  Approve = "approve",
  Reject = "reject",
}

// Global admin decides a pending void request: approve voids the order, reject cancels it (past charges stand).
const ReviewVoidRequestModal = (props: ReviewVoidRequestModalProps) => {
  const { order, onClose, onSuccess } = props;
  const voidMeta = order.meta?.void;

  const { snackbarComponent, setSnackbarError } = useSnackbar();

  const [info, setInfo] = useState<IOrderVoidInfo>();
  const [decision, setDecision] = useState<Decision>();
  const [notes, setNotes] = useState<string>("");

  // notes explain a rejection to the requester; optional on approval
  const notesRequired = decision === Decision.Reject;
  const [loading, setLoading] = useState<boolean>(true);
  const [completed, setCompleted] = useState<Decision>();

  useEffect(() => {
    OrderService.getCancelOptions(order.id)
      .then(({ data }) => setInfo(data.info))
      .catch(() => setSnackbarError())
      .finally(() => setLoading(false));
  }, [order.id, setSnackbarError]);

  const handleSubmit = () => {
    if (!decision || (notesRequired && !notes.trim())) return;

    const data = { orderId: order.id, comment: notes.trim() };
    const request =
      decision === Decision.Approve
        ? OrderService.void(data)
        : OrderService.rejectVoidRequest(data);

    setLoading(true);
    request
      .then(() => {
        onSuccess();
        setCompleted(decision);
      })
      .catch(() => setSnackbarError())
      .finally(() => setLoading(false));
  };

  const sensor = info?.sensorName
    ? `${info.sensorName}${info.sensorStatus ? ` - ${info.sensorStatus}` : ""}${
        info.sensorStatusOn ? ` ${formatDate(info.sensorStatusOn)}` : ""
      }`
    : "Not assigned";

  return (
    <Modal open onClose={onClose}>
      <>
        <div
          className={`absolute w-4/5 ${completed ? "max-w-[420px]" : "max-w-[540px]"} flex flex-col top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-md shadow-2xl bg-white px-8 py-7`}
        >
          <ButtonCloseModal
            className={completed ? "!top-5" : "!top-5 left-4"}
            color={Color.CloudBurst}
            onClick={onClose}
          />

          {completed ? (
            <div className="flex flex-col pt-1">
              <h1 className="text-cloudBurst text-lg font-bold mb-2">
                {completed === Decision.Approve
                  ? "Void request approved"
                  : "Void request rejected"}
              </h1>
              <p className="text-sm">
                Order #{order.orderNum}{" "}
                {completed === Decision.Approve
                  ? "has been voided."
                  : "void request has been rejected and status changed to Canceled. Past charges stand."}{" "}
                The requester has been notified by email.
              </p>
              <Button className="mt-5 self-end !bg-pelorous" onClick={onClose}>
                OK
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-center text-cloudBurst text-lg leading-5 font-bold mb-5">
                Review Order #{order.orderNum} Void Request
              </h1>

              <div className="rounded-md bg-aliceBlue3 px-4 py-3 text-sm">
                <p>
                  <b>Requested by</b> {voidMeta?.requestedBy} on{" "}
                  {voidMeta?.requestedOn
                    ? formatDate(voidMeta.requestedOn)
                    : ""}
                </p>
                <p className="mt-1">
                  <b>Reason:</b> {voidMeta?.requestReason}
                </p>
              </div>

              <div className="mt-3 rounded-md bg-aliceBlue3 px-4 py-3 text-sm">
                <p className="font-bold mb-1">Impact if approved</p>
                {info &&
                  getVoidChargeLines(info).map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                <p>
                  <b>Sensor:</b> {sensor}
                </p>
              </div>

              <div className="flex gap-3 mt-4">
                {[Decision.Approve, Decision.Reject].map((value) => (
                  <label
                    key={value}
                    className={`flex-1 flex items-center gap-3 rounded-lg border px-4 py-3 cursor-pointer ${
                      decision === value
                        ? "border-malibu bg-pattensBlue4/40"
                        : "border-pattensBlue2"
                    }`}
                  >
                    <input
                      type="radio"
                      name="void-decision"
                      className="w-5 h-5 accent-pelorous"
                      checked={decision === value}
                      onChange={() => setDecision(value)}
                    />
                    <b className="text-cloudBurst">
                      {value === Decision.Approve ? "Approve" : "Reject"}
                    </b>
                  </label>
                ))}
              </div>

              {decision && (
                <>
                  <div className="mt-4">
                    <InputField
                      required={notesRequired}
                      multiline
                      rows={3}
                      label="Review Notes"
                      name="notes"
                      placeholder={
                        notesRequired
                          ? "These notes are sent to the requester."
                          : "Optional. These notes are sent to the requester."
                      }
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>

                  <div className="flex gap-5 justify-center mt-6">
                    <Button
                      className="border-2 border-pelorous !bg-white !text-pelorous"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="!bg-pelorous"
                      disabled={notesRequired && !notes.trim()}
                      onClick={handleSubmit}
                    >
                      {decision === Decision.Approve
                        ? "Approve & Void Order"
                        : "Reject Request"}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}

          <Loading loading={loading} />
        </div>

        {snackbarComponent}
      </>
    </Modal>
  );
};

export default ReviewVoidRequestModal;
